import React from 'react';
import './MoneySavingPage.css';

interface MoneySavingPageProps {
  onNavigateToChatbot: () => void;
}

const MoneySavingPage: React.FC<MoneySavingPageProps> = ({ onNavigateToChatbot }) => {
  const handleExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <main className="money-saving-page">
      {/* Hero Section */}
      <section className="money-saving-hero">
        <div className="hero-content">
          <h1>Smart Ways to Save Money on Insurance</h1>
          <p>Discover proven strategies to reduce your insurance premiums without sacrificing coverage quality.</p>
        </div>
      </section>

      {/* Top Money-Saving Tips */}
      <section className="top-tips">
        <div className="container">
          <h2>Top Money-Saving Strategies</h2>
          <div className="tips-grid">
            <div className="tip-card featured">
              <div className="tip-icon">📦</div>
              <h3>Bundle Your Policies</h3>
              <div className="savings-amount">Save up to 25%</div>
              <p>Combine auto, home, and other insurance policies with the same provider for significant multi-policy discounts.</p>
              <ul>
                <li>Auto + Home: Average 15-20% savings</li>
                <li>Add renters/condo: Additional 5-10%</li>
                <li>Life insurance bundle: Extra 3-5%</li>
              </ul>
            </div>

            <div className="tip-card">
              <div className="tip-icon">🔍</div>
              <h3>Shop Around Annually</h3>
              <div className="savings-amount">Save up to $500+</div>
              <p>Compare quotes from multiple insurers every year. Rates change and new discounts become available.</p>
              <ul>
                <li>Get quotes from 3-5 different companies</li>
                <li>Check rates before your renewal</li>
                <li>Consider both online and traditional insurers</li>
              </ul>
            </div>

            <div className="tip-card">
              <div className="tip-icon">📊</div>
              <h3>Increase Your Deductible</h3>
              <div className="savings-amount">Save 10-40%</div>
              <p>Raising your deductible from $250 to $1,000 can significantly lower your premium payments.</p>
              <ul>
                <li>Auto: $500 to $1,000 deductible saves 15-30%</li>
                <li>Home: $500 to $2,500 saves 12-25%</li>
                <li>Ensure you have emergency funds to cover deductible</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Discount Opportunities */}
      <section className="discount-opportunities">
        <div className="container">
          <h2>Available Discounts & How to Qualify</h2>
          <div className="discounts-grid">
            <div className="discount-category">
              <h3>🚗 Auto Insurance Discounts</h3>
              <div className="discount-list">
                <div className="discount-item">
                  <div className="discount-name">Safe Driver Discount</div>
                  <div className="discount-savings">5-20%</div>
                  <div className="discount-description">No accidents or moving violations for 3-5 years</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Good Student Discount</div>
                  <div className="discount-savings">10-15%</div>
                  <div className="discount-description">Students under 25 with B average or better</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Low Mileage Discount</div>
                  <div className="discount-savings">5-15%</div>
                  <div className="discount-description">Drive less than 7,500-10,000 miles per year</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Safety Features Discount</div>
                  <div className="discount-savings">5-10%</div>
                  <div className="discount-description">Anti-theft devices, airbags, ABS brakes</div>
                </div>
              </div>
            </div>

            <div className="discount-category">
              <h3>🏠 Home Insurance Discounts</h3>
              <div className="discount-list">
                <div className="discount-item">
                  <div className="discount-name">Security System Discount</div>
                  <div className="discount-savings">5-20%</div>
                  <div className="discount-description">Monitored alarm systems and security cameras</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Fire Safety Discount</div>
                  <div className="discount-savings">5-15%</div>
                  <div className="discount-description">Smoke detectors, fire extinguishers, sprinkler systems</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">New Home Discount</div>
                  <div className="discount-savings">8-15%</div>
                  <div className="discount-description">Homes built within the last 10 years</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Claims-Free Discount</div>
                  <div className="discount-savings">5-25%</div>
                  <div className="discount-description">No claims filed for 3-5 consecutive years</div>
                </div>
              </div>
            </div>

            <div className="discount-category">
              <h3>👥 Lifestyle Discounts</h3>
              <div className="discount-list">
                <div className="discount-item">
                  <div className="discount-name">Military/Veteran Discount</div>
                  <div className="discount-savings">5-15%</div>
                  <div className="discount-description">Active duty, reserves, and veterans</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Professional Discount</div>
                  <div className="discount-savings">3-10%</div>
                  <div className="discount-description">Teachers, engineers, doctors, and other professionals</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Senior Citizen Discount</div>
                  <div className="discount-savings">5-10%</div>
                  <div className="discount-description">Usually available at age 55 or 65</div>
                </div>
                <div className="discount-item">
                  <div className="discount-name">Group/Association Discount</div>
                  <div className="discount-savings">5-12%</div>
                  <div className="discount-description">Alumni associations, professional groups, employers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Score Impact */}
      <section className="credit-score-section">
        <div className="container">
          <div className="credit-content">
            <div className="credit-text">
              <h2>How Your Credit Score Affects Insurance Rates</h2>
              <p>In most states, insurers use credit-based insurance scores to help determine your premium. A better credit score often means lower insurance costs.</p>
              
              <div className="credit-tips">
                <h4>Improve Your Credit Score:</h4>
                <ul>
                  <li>Pay all bills on time, every time</li>
                  <li>Keep credit card balances low (under 30% of limit)</li>
                  <li>Don't close old credit cards</li>
                  <li>Monitor your credit report for errors</li>
                  <li>Limit new credit applications</li>
                </ul>
              </div>

              <div className="credit-impact">
                <h4>Potential Savings by Credit Score Range:</h4>
                <div className="score-ranges">
                  <div className="score-range excellent">
                    <span className="score">800+</span>
                    <span className="impact">Excellent - Best rates available</span>
                  </div>
                  <div className="score-range good">
                    <span className="score">700-799</span>
                    <span className="impact">Good - 10-15% higher than excellent</span>
                  </div>
                  <div className="score-range fair">
                    <span className="score">600-699</span>
                    <span className="impact">Fair - 20-30% higher than excellent</span>
                  </div>
                  <div className="score-range poor">
                    <span className="score">Below 600</span>
                    <span className="impact">Poor - 40-50% higher than excellent</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="credit-image">
              <div className="placeholder-image">📊💳</div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Optimization */}
      <section className="coverage-optimization">
        <div className="container">
          <h2>Smart Coverage Decisions</h2>
          <div className="optimization-grid">
            <div className="optimization-card">
              <div className="optimization-icon">⚖️</div>
              <h3>Right-Size Your Coverage</h3>
              <p>Don't over-insure or under-insure. Review your coverage limits annually to ensure they match your current needs.</p>
              <ul>
                <li>Update coverage when you pay off loans</li>
                <li>Increase limits as your assets grow</li>
                <li>Consider dropping collision on older vehicles</li>
                <li>Review liability limits based on net worth</li>
              </ul>
            </div>

            <div className="optimization-card">
              <div className="optimization-icon">🎯</div>
              <h3>Usage-Based Insurance</h3>
              <p>Pay-per-mile or behavior-based insurance can save safe drivers 10-40% on auto insurance.</p>
              <ul>
                <li>Telematics programs track driving habits</li>
                <li>Safe drivers get rewarded with discounts</li>
                <li>Low-mileage drivers pay less</li>
                <li>Some programs offer immediate discounts</li>
              </ul>
            </div>

            <div className="optimization-card">
              <div className="optimization-icon">💡</div>
              <h3>Policy Timing</h3>
              <p>When you buy insurance and how you pay can affect your rates.</p>
              <ul>
                <li>Pay annually vs. monthly to avoid fees</li>
                <li>Set up automatic payments for discounts</li>
                <li>Renew on time to avoid lapses</li>
                <li>Consider seasonal rate changes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Avoiding Common Mistakes */}
      <section className="common-mistakes">
        <div className="container">
          <h2>Avoid These Costly Mistakes</h2>
          <div className="mistakes-grid">
            <div className="mistake-item">
              <div className="mistake-icon">❌</div>
              <h3>Choosing the Cheapest Policy</h3>
              <p>The lowest price doesn't always mean the best value. Consider coverage quality, customer service, and claim handling reputation.</p>
            </div>

            <div className="mistake-item">
              <div className="mistake-icon">❌</div>
              <h3>Not Updating Your Policy</h3>
              <p>Life changes affect your insurance needs. Update your policy when you move, get married, buy a car, or have major life events.</p>
            </div>

            <div className="mistake-item">
              <div className="mistake-icon">❌</div>
              <h3>Forgetting to Ask About Discounts</h3>
              <p>Insurance companies don't always volunteer available discounts. Ask your agent about all possible savings opportunities.</p>
            </div>

            <div className="mistake-item">
              <div className="mistake-icon">❌</div>
              <h3>Letting Policies Lapse</h3>
              <p>Coverage gaps can result in higher rates when you reapply. Continuous coverage often qualifies for loyalty discounts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="savings-calculator">
        <div className="container">
          <div className="calculator-content">
            <h2>Potential Annual Savings</h2>
            <p>See how much you could save by implementing these money-saving strategies:</p>
            
            <div className="savings-examples">
              <div className="example-scenario">
                <h3>Single Driver with Auto Insurance</h3>
                <div className="scenario-savings">
                  <div className="saving-item">
                    <span className="saving-strategy">Increase deductible $500 → $1,000</span>
                    <span className="saving-amount">-$200/year</span>
                  </div>
                  <div className="saving-item">
                    <span className="saving-strategy">Add safety features discount</span>
                    <span className="saving-amount">-$75/year</span>
                  </div>
                  <div className="saving-item">
                    <span className="saving-strategy">Shop around and switch</span>
                    <span className="saving-amount">-$300/year</span>
                  </div>
                  <div className="total-savings">
                    <span>Total Potential Savings:</span>
                    <span className="total-amount">$575/year</span>
                  </div>
                </div>
              </div>

              <div className="example-scenario">
                <h3>Homeowner with Auto & Home Insurance</h3>
                <div className="scenario-savings">
                  <div className="saving-item">
                    <span className="saving-strategy">Bundle auto + home policies</span>
                    <span className="saving-amount">-$400/year</span>
                  </div>
                  <div className="saving-item">
                    <span className="saving-strategy">Security system discount</span>
                    <span className="saving-amount">-$150/year</span>
                  </div>
                  <div className="saving-item">
                    <span className="saving-strategy">Increase home deductible</span>
                    <span className="saving-amount">-$200/year</span>
                  </div>
                  <div className="saving-item">
                    <span className="saving-strategy">Safe driver discount</span>
                    <span className="saving-amount">-$180/year</span>
                  </div>
                  <div className="total-savings">
                    <span>Total Potential Savings:</span>
                    <span className="total-amount">$930/year</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="resources-section">
        <div className="container">
          <h2>Money-Saving Resources</h2>
          <div className="resources-grid">
            <div className="resource-card">
              <div className="resource-icon">🏛️</div>
              <h3>Government Resources</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.consumerreports.org/cro/car-insurance.htm')}
                  className="resource-link"
                >
                  Consumer Reports - Insurance Guides
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.naic.org/')}
                  className="resource-link"
                >
                  NAIC - National Association of Insurance Commissioners
                </button>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon">💳</div>
              <h3>Credit Monitoring</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.annualcreditreport.com/')}
                  className="resource-link"
                >
                  Free Annual Credit Report
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.creditkarma.com/')}
                  className="resource-link"
                >
                  Credit Karma - Free Credit Monitoring
                </button>
              </div>
            </div>

            <div className="resource-card">
              <div className="resource-icon">📊</div>
              <h3>Comparison Tools</h3>
              <div className="resource-links">
                <button
                  onClick={() => handleExternalLink('https://www.iii.org/')}
                  className="resource-link"
                >
                  Insurance Information Institute
                </button>
                <button
                  onClick={() => handleExternalLink('https://www.nerdwallet.com/insurance')}
                  className="resource-link"
                >
                  NerdWallet Insurance Center
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Take Action */}
      <section className="take-action">
        <div className="container">
          <div className="action-content">
            <h2>Start Saving Today</h2>
            <p>Don't wait to start saving money on your insurance. Even small changes can add up to significant annual savings.</p>
            <div className="action-buttons">
              <button 
                className="btn-primary"
                onClick={onNavigateToChatbot}
              >
                Ask Our AI About Savings
              </button>
              <button className="btn-secondary">
                Get Money-Saving Quote
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MoneySavingPage;